import axios, { AxiosResponse } from 'axios';
import { useCustomerContext } from 'pages/Customer/CustomerContext';
import React, { createContext, useContext, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { createEmissor } from 'services/installation/installation';
import { CreateEmissorInstallationResponse } from 'services/interfaces/installation/installation';
import CreateEmissorModal from './CreateEmissorModal';
import {
  buildEmissorPayload,
  LegacyEmissorJson,
} from './helpers/buildEmissorPayload';
import validateEmissorForm, { IErrors } from './validation';

interface CreateEmissorModalProps {
  isOpen: boolean;
  onClose(): void;
}

interface CreateEmissorModalContextData {
  loading: boolean;
  fileName: string;
  errors: IErrors;
  handleFileChange(file: File | null): void;
  handleSubmit(): void;
}

const Context = createContext<CreateEmissorModalContextData>(
  {} as CreateEmissorModalContextData,
);

export const useCreateEmissorModalContext = () => useContext(Context);

function readFileAsText(file: File, encoding: string): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () =>
      reject(reader.error ?? new Error('Falha ao ler arquivo'));
    reader.readAsText(file, encoding);
  });
}

async function parseEmissorFile(file: File): Promise<LegacyEmissorJson> {
  try {
    return JSON.parse(await readFileAsText(file, 'UTF-8'));
  } catch {
    // ponytail: legado usa windows-1252; teto = encoding raro sem fallback extra
    return JSON.parse(await readFileAsText(file, 'windows-1252'));
  }
}

const CreateEmissorModalContext: React.FC<CreateEmissorModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { customer, loadCustomer, loadApps } = useCustomerContext();
  const [loading, setLoading] = useState(false);
  const [fileName, setFileName] = useState('');
  const [parsedJson, setParsedJson] = useState<LegacyEmissorJson | null>(null);
  const [errors, setErrors] = useState<IErrors>({});

  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
      setFileName('');
      setParsedJson(null);
      setErrors({});
    }
  }, [isOpen]);

  const handleFileChange = (file: File | null) => {
    setErrors({});
    setParsedJson(null);
    setFileName(file?.name ?? '');

    if (!file) return;

    parseEmissorFile(file)
      .then((json) => {
        setParsedJson(json);
      })
      .catch(() => {
        setParsedJson(null);
        setErrors({ file: 'Arquivo JSON inválido ou ilegível' });
      });
  };

  const handleSubmit = () => {
    const validationErrors = validateEmissorForm({
      parsedJson,
      customerId: customer?.id,
    });
    setErrors(validationErrors);

    if (Object.values(validationErrors).length || !parsedJson) return;

    const payload = buildEmissorPayload(parsedJson, customer.id);

    setLoading(true);
    createEmissor(payload)
      .then((response: AxiosResponse<CreateEmissorInstallationResponse>) => {
        toast.success(`Emissor criado com sucesso. IDI: ${response.data.idi}`);
        onClose();
        loadCustomer();
        loadApps();
      })
      .catch((error: unknown) => {
        if (axios.isAxiosError(error) && error.response?.status === 422) {
          toast.error('CNPJ já vinculado a um emissor');
          return;
        }
        toast.error('Falha ao criar emissor');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const data: CreateEmissorModalContextData = {
    loading,
    fileName,
    errors,
    handleFileChange,
    handleSubmit,
  };

  return (
    <Context.Provider value={data}>
      <CreateEmissorModal isOpen={isOpen} onClose={onClose} />
    </Context.Provider>
  );
};

export default CreateEmissorModalContext;
